import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { videoData, articleContent, categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videoData.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">该视频可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const article = articleContent.find((a) => a.id === video.id);
  const category = categories.find((c) => c.slug === video.category);
  const relatedVideos = videoData
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: video.title,
          description: video.description,
          datePublished: video.date,
          author: {
            '@type': 'Person',
            name: video.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '小蓝视频',
          },
        })}
      </script>
      <Navbar />
      <main className="pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
            {video.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
            <div className="flex items-center gap-2">
              <img
                src={video.authorAvatar}
                alt={video.author}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="font-medium text-foreground-700">{video.author}</span>
            </div>
            <span className="text-foreground-300">|</span>
            <span>{video.date}</span>
            <span className="text-foreground-300">|</span>
            <span>{video.views}次观看</span>
            {category && (
              <>
                <span className="text-foreground-300">|</span>
                <Link
                  to={`/category/${category.slug}`}
                  className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
                >
                  {category.name}
                </Link>
              </>
            )}
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
            <img
              src={video.coverImage}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-16 h-16 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors duration-200 hover:scale-110 transform">
                <i className="ri-play-fill text-background-50 text-3xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 text-background-50 text-sm rounded-md font-medium">
              {video.duration}
            </div>
          </div>

          <div className="flex items-center justify-between mb-8 pb-6 border-b border-background-200/70">
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200">
                <i className="ri-thumb-up-line text-lg"></i>
                点赞
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200">
                <i className="ri-star-line text-lg"></i>
                收藏
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200">
                <i className="ri-share-forward-line text-lg"></i>
                分享
              </button>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
              <i className="ri-download-cloud-2-line text-lg"></i>
              下载视频
            </button>
          </div>

          {article && (
            <div
              className="prose prose-sm md:prose-base max-w-none mb-12 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          )}
        </article>

        {relatedVideos.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12">
            <h3 className="text-xl font-bold text-foreground-950 font-heading mb-6">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedVideos.map((v) => (
                <VideoCard
                  key={v.id}
                  id={v.id}
                  title={v.title}
                  coverImage={v.coverImage}
                  duration={v.duration}
                  views={v.views}
                  author={v.author}
                  authorAvatar={v.authorAvatar}
                  category={v.category}
                />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}